<template>
  <AppValidationGroup v-slot="{ validate }">
    <div class="space-y-4 pb-4">
      <p v-if="mode === 'create'" class="text-subtle">
        Ange personnummer och välj vilka roller personen ska få.
      </p>

      <div v-if="formError" class="error-box rounded-md px-3 py-2">
        {{ formError }}
      </div>

      <div v-if="mode === 'create'" class="max-w-sm">
        <AppTextField
          v-model="form.personalNumber"
          trim
          label="Personnummer"
          placeholder="198010209999"
          :rules="[required(), personalNumber()]"
        />
      </div>

      <div class="space-y-2">
        <p v-if="mode === 'edit'" class="font-medium">
          Uppdaterar roller för {{ editingPersonName }}
        </p>
        <AppTable>
          <thead>
            <tr>
              <th>Roll</th>
              <th>Beskrivning</th>
              <th>Tilldela</th>
            </tr>
          </thead>
          <tbody>
            <AppTableRow v-for="scope in personnelScopes" :key="scope">
              <AppTableCell>
                {{ translateScope(scope) }}
              </AppTableCell>
              <AppTableCell>
                {{ scopeDescriptions[scope] }}
              </AppTableCell>
              <AppTableCell class="text-center" action>
                <AppCheckbox
                  :model-value="isScopeChecked(scope)"
                  :disabled="isScopeRequired(scope)"
                  :aria-label="`Välj rollen ${translateScope(scope)}`"
                  @update:model-value="(checked) => setScope(scope, checked)"
                />
              </AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>
      </div>

      <div class="flex justify-end gap-2 pt-4">
        <AppButton :disabled="isSubmitting" @click="emit('close')">Avbryt</AppButton>
        <AppButton
          color="blue"
          medium
          :loading="isSubmitting"
          :disabled="isSubmitDisabled"
          @click="validate(() => void submitForm())"
        >
          {{ mode === 'create' ? 'Spara personal' : 'Spara ändringar' }}
        </AppButton>
      </div>
    </div>
  </AppValidationGroup>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { parsePersonalNumber, personalNumber, required } from '@staff/shared'
import {
  AppButton,
  AppCheckbox,
  AppTable,
  AppTableCell,
  AppTableRow,
  AppTextField,
  AppValidationGroup,
} from '@staff/ui'

import {
  createPersonnel,
  personnelScopes,
  updatePersonnel,
  type PersonnelListItem,
  type PersonnelScope,
} from '@/services/personnel'

const REQUIRED_SCOPE: PersonnelScope = 'broker'

const scopeLabels: Record<PersonnelScope, string> = {
  broker: 'Handläggare',
  brokerPersonnelManager: 'Personalansvarig',
  brokerPricingAdministrator: 'Prissättningsansvarig',
}

const scopeDescriptions: Record<PersonnelScope, string> = {
  broker: 'Grundbehörighet för samtlig personal',
  brokerPersonnelManager: 'Rätt att lägga till personal och sätta behörigheter',
  brokerPricingAdministrator: 'Rätt att ändra listräntor',
}

const props = defineProps<{
  mode: 'create' | 'edit'
  person?: PersonnelListItem
  onSaved: () => void | Promise<void>
}>()

const emit = defineEmits<{
  (event: 'close'): void
}>()

const ensureRequiredScope = (scopes: PersonnelScope[]) =>
  scopes.includes(REQUIRED_SCOPE) ? scopes : [REQUIRED_SCOPE, ...scopes]

const getScopes = (person?: PersonnelListItem): PersonnelScope[] => {
  return person?.scope.length ? ensureRequiredScope([...person.scope]) : [REQUIRED_SCOPE]
}

const form = ref({
  personalNumber: props.person?.user?.personalNumber?.toString() || '',
  scopes: getScopes(props.person),
})

const isSubmitting = ref(false)
const formError = ref('')

const editingPersonName = computed(() => {
  if (!props.person) {
    return 'vald person'
  }

  return `${props.person.user?.givenName || ''} ${props.person.user?.surname || ''}`.trim() || 'vald person'
})

const isSubmitDisabled = computed(() => {
  if (isSubmitting.value || form.value.scopes.length === 0) {
    return true
  }

  if (props.mode === 'create') {
    return !form.value.personalNumber.trim()
  }

  return !props.person?.id
})

const translateScope = (scope: PersonnelScope) => scopeLabels[scope] || scope
const isScopeSelected = (scope: PersonnelScope) => form.value.scopes.includes(scope)
const isScopeRequired = (scope: PersonnelScope) => scope === REQUIRED_SCOPE
const isScopeChecked = (scope: PersonnelScope) => isScopeRequired(scope) || isScopeSelected(scope)

const setScope = (scope: PersonnelScope, checked: boolean) => {
  if (isScopeRequired(scope)) {
    return
  }

  if (checked) {
    form.value.scopes = ensureRequiredScope([...form.value.scopes, scope])
    return
  }

  form.value.scopes = ensureRequiredScope(form.value.scopes.filter((currentScope) => currentScope !== scope))
}

const submitForm = async () => {
  if (isSubmitDisabled.value) {
    return
  }

  isSubmitting.value = true
  formError.value = ''

  try {
    if (props.mode === 'create') {
      await createPersonnel({
        personalNumber: parsePersonalNumber(form.value.personalNumber),
        scope: form.value.scopes,
      })
    } else if (props.person?.id) {
      await updatePersonnel(props.person.id, {
        scope: form.value.scopes,
      })
    }

    await props.onSaved()
    emit('close')
  } catch (err) {
    formError.value = err instanceof Error ? err.message : 'Det gick inte att spara personal.'
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped lang="scss">
.error-box {
  border: 1px solid var(--color-danger-emphasis);
  background-color: var(--color-danger-subtle);
  color: var(--color-danger-on-subtle);
}
</style>
